/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { transparentize } from 'polished'
import { Slide, SlideshowRef } from 'react-slideshow-image'
import { ToastOptions, toast } from 'react-toastify'
import styled, { css } from 'styled-components'

import heroBgMobileImage from 'src/assets/hero-bg-mobile.png'
import { ToastContent } from 'src/components/Common/Toast'
import ToggleSwitch from 'src/components/Input/ToggleSwitch'
import WalletInstall from 'src/components/Modal/views/WalletInstall'
import {
  CHAIN_MAP,
  DONATION_SGD_AMOUNT_MIN,
  DONATION_SGD_AMOUNT_MIN_ALT,
  SUPPORTED_CHAINS
} from 'src/constants'
import {
  useDonationIdentityCollectorContext,
  useDonorIdentityCollectorModal
} from 'src/features/donation/donation-identity-collector'
// import { useTaxDeductible } from 'src/features/donation/donation-tax'
import { useModalV2Context } from 'src/features/modal-v2'
import { useMediaScreenType } from 'src/hooks/useMediaScreenType'

import heroBgImage from '../../assets/hero-bg.png'
import slider1 from '../../assets/slider1.png'
import slider2 from '../../assets/slider2.png'
import slider3 from '../../assets/slider3.png'
import slider4 from '../../assets/slider4.png'
import slider5 from '../../assets/slider5.png'
import { PrimaryButton } from '../../components/Button'
import FlexBox from '../../components/Common/FlexBox'
import Icon from '../../components/Icon'
import { InputText } from '../../components/Input'
import { ErrorKey } from '../../components/Input/Text'
// import { DonateMethodDialog } from '../../components/Modal'
import { ERROR_MESSAGE } from '../../constants/errors'
// import { useModalContext } from '../../contexts/ModalContext'
import { useThemeContext } from '../../contexts/ThemeContext'
import { useWeb3Context } from '../../contexts/Web3Context'
import { pxToEm, pxToVw } from '../../functions/units'
import {
  getLocalStorageDonationWillClaimCoupon,
  removeLocalStorageDonationAmount,
  setLocalStorageDonationAmount,
  setLocalStorageDonationWillClaimCoupon
} from '../../services/donation'
import { CouldBe, Nullable } from '../../types/main'

const HomeContainer = () => {
  // const { openModal } = useModalContext()
  const { openModal: openModalV2 } = useModalV2Context()
  const { active, account, activate, chainId } = useWeb3Context()
  const { isMobile } = useMediaScreenType()
  const { colors } = useThemeContext()

  // const openModalArgsRef = useRef<Nullable<Parameters<ModalContextType['openModal']>>>(null)
  const ethereum = useRef((window as any).ethereum)

  const [isInit, setIsInit] = useState<boolean>(true)
  const [donationAmount, setDonationAmount] = useState<CouldBe<string>>('')
  const [donationAmountError, setDonationAmountError] = useState<Nullable<ErrorKey>>(null)
  const [shouldOpenModalWhenActivated, setShouldOpenModalWhenActivated] = useState<boolean>(false)

  /*
   * Whether user will set their donation a tax-deductible or not
   */
  // const { taxDeductible, onTaxDeductibleChange } = useTaxDeductible()
  // const { donorIdentity } = useDonationIdentityCollectorContext()
  const { identityIsValid } = useDonationIdentityCollectorContext()
  const { openCollectorModal, openIdentityConfirmModal } = useDonorIdentityCollectorModal()

  const handleDonationAmountChange = useCallback(
    (value: string) => {
      setDonationAmount(value)

      if (isInit) {
        setIsInit(false)
      }

      if (!value) {
        return removeLocalStorageDonationAmount()
      }

      setLocalStorageDonationAmount(value)
    },
    [isInit]
  )

  useEffect(() => {
    if (active && account && shouldOpenModalWhenActivated) {
      // && openModalArgsRef.current !== null

      if (!identityIsValid) {
        openCollectorModal()
      } else {
        openIdentityConfirmModal()
      }

      /*openModal(<DonateMethodDialog />, 'donate-method', {
        dialogTitle: 'Choose donation method'
      })*/
      setShouldOpenModalWhenActivated(false)
      // openModalArgsRef.current = null
    }
  }, [
    account,
    active,
    identityIsValid,
    openCollectorModal,
    openIdentityConfirmModal,
    shouldOpenModalWhenActivated
  ])

  const handleDonateButtonClick = useCallback(() => {
    const willClaimCoupon = getLocalStorageDonationWillClaimCoupon()

    if (donationAmountError && donationAmountError === 'AMOUNT_MAX_EXCEEDED') {
      return openModalV2({
        content: (
          <StyledTipsArticle>
            <p>
              We currently accept donations of not more than <strong>SGD $20,000</strong> per
              transaction. If you wish to donate more than that, please contact The Good Blockchain
              at <a href="mailto:doinggood@thegoodblockchain.sg">doinggood@thegoodblockchain.sg</a>.
            </p>
          </StyledTipsArticle>
        ),
        showDialogHeader: false,
        showDialogFooter: true
      })
    }

    if (!ethereum.current && willClaimCoupon && Number(willClaimCoupon) === 1) {
      return openModalV2(
        {
          content: <WalletInstall />,
          showDialogHeader: false,
          showDialogFooter: true
        },
        'metamask-install-guide'
      )
    }

    /*const modalConfig: Partial<ModalConfig> = {
      dialogTitle: 'Choose donation method'
    }*/

    if (willClaimCoupon && Number(willClaimCoupon) === 1 && (!active || !account)) {
      // openModalArgsRef.current = [<DonateMethodDialog />, 'donate-method', modalConfig]
      setShouldOpenModalWhenActivated(true)
      activate()
      return
    }

    /*if (taxDeductible && !donorIdentity) {
      return openCollectorModal()
    } else if (taxDeductible && donorIdentity) {
      return openIdentityConfirmModal()
    }*/

    if (!identityIsValid) {
      openCollectorModal()
    } else {
      openIdentityConfirmModal()
    }

    /*openModal(<DonateMethodDialog />, 'donate-method', {
      dialogTitle: 'Choose donation method'
    })*/
  }, [
    account,
    activate,
    active,
    donationAmountError,
    identityIsValid,
    openCollectorModal,
    openIdentityConfirmModal,
    openModalV2
  ])

  /*
   * Whether user will claim coupon or not as a result of their donation
   */
  const [participateInNft, setParticipateInNft] = useState<boolean>(true)

  useEffect(() => {
    let willClaimCoupon = getLocalStorageDonationWillClaimCoupon()
    if (willClaimCoupon === null) {
      setLocalStorageDonationWillClaimCoupon(true)
      setParticipateInNft(true)
      return
    }

    setParticipateInNft(Number(willClaimCoupon) === 1)
  }, [])

  const handleDonateWithoutClaimingChange = useCallback(
    (value: boolean) => {
      setParticipateInNft(value)
      setLocalStorageDonationWillClaimCoupon(value)

      if (value === true && !chainId) {
        const toastOptions: ToastOptions = {
          toastId: 'UnsupportedChainIdError',
          closeOnClick: false,
          autoClose: false,
          type: 'info'
        }
        const header = ERROR_MESSAGE.METAMASK_CHAIN_UNSUPPORTED_HEADER
        const detail = ERROR_MESSAGE.METAMASK_CHAIN_UNSUPPORTED_DETAIL
        const additionalContent = (
          <details style={{ display: 'block', marginTop: 8 }}>
            <summary>
              <small>Supported chains</small>
            </summary>
            {SUPPORTED_CHAINS.map((chainId) => (
              <small style={{ display: 'block' }} key={`chain_${chainId}`}>
                {CHAIN_MAP[chainId].name} ({chainId})
              </small>
            ))}
          </details>
        )

        toast(<ToastContent message={{ header, detail, additionalContent }} />, toastOptions)
      }
    },
    [chainId]
  )

  const donationAmountErrorMessage = useMemo(() => {
    if (!!donationAmountError) {
      if (
        donationAmountError === 'AMOUNT_MIN_EXCEEDED' ||
        donationAmountError === 'AMOUNT_IS_ZERO'
      ) {
        const minAmount = !participateInNft ? DONATION_SGD_AMOUNT_MIN_ALT : DONATION_SGD_AMOUNT_MIN
        return ERROR_MESSAGE.AMOUNT_MIN_EXCEEDED.replace('<min_amount>', minAmount.toString())
      } else if (donationAmountError !== 'AMOUNT_MAX_EXCEEDED') {
        return ERROR_MESSAGE[donationAmountError]
      }
    }

    return null
  }, [donationAmountError, participateInNft])

  const slideshowRef = useRef<SlideshowRef>(null)

  return (
    <StyledContainer>
      <StyledHero>
        <StyledSlider>
          <Slide duration={5000} ref={slideshowRef}>
            <StyledSliderImage src={slider1} />
            <StyledSliderImage src={slider2} />
            <StyledSliderImage src={slider3} />
            <StyledSliderImage src={slider4} />
            <StyledSliderImage src={slider5} />
          </Slide>
        </StyledSlider>
        <StyledEasterEggs>
          <div onClick={() => slideshowRef.current?.goTo(0)} />
          <div onClick={() => slideshowRef.current?.goTo(1)} />
          <div onClick={() => slideshowRef.current?.goTo(2)} />
          <div onClick={() => slideshowRef.current?.goTo(3)} />
        </StyledEasterEggs>

        <StyledArticle>
          <h1>
            <span>National Day 2022 NFT</span> <span>Fundraising Campaign:</span>
            <br />
            <strong>In Support of Enabling Village</strong>
          </h1>

          <p>
            Donations can be made in SGD or cryptocurrency. Every dollar donated gets you 1 redemption token. 
            NFTs are available for redemption at 399, 999, 1999. 4999, and 9999 tokens. 
            All donations of at least $200 will also receive tax deduction.
          </p>

          <FlexBox flexDirection={isMobile ? 'column' : 'row'} rowGap={isMobile ? 18 : undefined}>
            <StyledFlexForm flexDirection="column" rowGap={12}>
              <StyledFlexFieldsGroup columnGap={12}>
                <StyledAmountInputWrap alignItems="stretch" flex={isMobile ? 1 : undefined}>
                  <StyledInputIconWrap
                    $error={!!donationAmountError && donationAmountError !== 'AMOUNT_MAX_EXCEEDED'}
                    alignItems="center"
                  >
                    <Icon icon="currency-dollar" color={colors.primary} size={22} />
                  </StyledInputIconWrap>

                  <StyledInputText
                    placeholder="Donation ($SGD)"
                    required
                    // useTaxDeduction={taxDeductible || false}
                    useParticipateInNft={participateInNft}
                    validator={/^[1-9]\d*(?:,\d{3})*(?:\.\d{1,2})?$/}
                    value={donationAmount}
                    onChange={handleDonationAmountChange}
                    onError={setDonationAmountError}
                  />
                </StyledAmountInputWrap>
                <StyledAmountButton
                  size="large"
                  outlined
                  onClick={() => handleDonationAmountChange('399')}
                >
                  $399
                </StyledAmountButton>
                <StyledAmountButton
                  size="large"
                  outlined
                  onClick={() => handleDonationAmountChange('1999')}
                >
                  $1999
                </StyledAmountButton>
                <StyledAmountButton
                  size="large"
                  outlined
                  onClick={() => handleDonationAmountChange('9999')}
                >
                  $9999
                </StyledAmountButton>
              </StyledFlexFieldsGroup>

              {!!donationAmountError && (
                <StyledFormError>{donationAmountErrorMessage}</StyledFormError>
              )}
            </StyledFlexForm>

            <StyledButtonWrap flexDirection="column" rowGap={12}>
              <div>
                <StyledButton
                  size="large"
                  disabled
                  onClick={handleDonateButtonClick}
                >
                  Already ended
                </StyledButton>
              </div>

              <FlexBox alignItems="center" columnGap={6}>
                <FlexBox as="label" alignItems="center" columnGap={6}>
                  <ToggleSwitch
                    checked={participateInNft}
                    onChange={handleDonateWithoutClaimingChange}
                  />
                  Receive tokens for NFT Redemption
                </FlexBox>

                <StyledTipsIcon
                  onClick={() => {
                    return openModalV2(
                      {
                        content: (
                          <StyledTipsArticle>
                            <p>
                              Your wallet must be connected in order to receive STAMP tokens. You
                              will receive a proportionate amount of STAMP token (1 SGD = 1 STAMP
                              token) when you make a donation. STAMP tokens can be used for
                              redemption of NFT in the Redemption Portal.
                            </p>
                            <p>
                              You have the option to opt-out should you not wish to participate in
                              the redemption of any NFT.
                            </p>
                          </StyledTipsArticle>
                        ),
                        dialogTitle: 'NFT Participation and Redemption'
                      },
                      'anonymous-donation-tips'
                    )
                  }}
                >
                  <StyledQuestionCircle icon="question-circle" size={24} color="#d3d3d3" />
                </StyledTipsIcon>
              </FlexBox>
            </StyledButtonWrap>
          </FlexBox>
        </StyledArticle>
      </StyledHero>
    </StyledContainer>
  )
}

export default HomeContainer

const StyledContainer = styled.div`
  margin-inline: -${pxToVw(250)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-inline: -${pxToVw(60, 720)};
    `}
  `}
`

const StyledHero = styled.div`
  position: relative;
  height: ${pxToVw(903)};
  background: url(${heroBgImage}) no-repeat bottom / contain;

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      height: auto;
      // background: url(${heroBgMobileImage}) no-repeat center ${pxToVw(840, 720)} / contain;
      background: url(${heroBgMobileImage}) no-repeat center bottom / contain;
      padding-bottom: ${pxToVw(1443, 720)};
    `}
  `}
`

const StyledArticle = styled.article`
  padding-left: ${pxToVw(250)};
  padding-top: ${pxToVw(88)};
  max-width: ${pxToVw(1000)};

  > h1 {
    color: ${({ theme }) => theme.colors.gray700};
    font-size: ${pxToVw(65)};
    font-weight: 700;
    line-height: ${({ theme }) => pxToEm(76, theme.units.fontSize.header1)};
    margin-top: 0;
    margin-bottom: ${pxToVw(14)};

    > span:last-of-type {
      letter-spacing: 0.03em;
    }

    > strong {
      font-size: ${pxToVw(61)};
      color: ${({ theme }) => theme.colors.primary500};
      letter-spacing: -0.035em;
    }
  }

  > p {
    font-size: ${({ theme }) => pxToVw(theme.units.fontSize.body1)};
    line-height: ${({ theme }) => pxToEm(28, theme.units.fontSize.body1)};
    color: ${({ theme }) => theme.colors.gray600};
    margin-bottom: ${pxToVw(26)};
    margin-top: 0;
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      // padding: ${pxToVw(51, 720)} ${pxToVw(60, 720)} ${pxToVw(727, 720)};
      padding: ${pxToVw(51, 720)} ${pxToVw(60, 720)} 0;
      max-width: unset;
      width: 100%;
      position: absolute;
      top: 0;
      left: 0;

      > h1 {
        font-size: ${pxToVw(60, 720)};
        margin-bottom: ${pxToVw(14, 720)};

        > span:first-of-type {
          letter-spacing: -0.03em;
        }
        > span:last-of-type {
          letter-spacing: -0.025em;
        }

        > strong {
          font-size: ${pxToVw(60, 720)};

        }
      }

      > p {
        font-size: ${pxToVw(25, 720)};
      }
    `}
  `}
`

const StyledFlexForm = styled(FlexBox)`
  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      width: 100%;
    `}
  `}
`

const StyledFlexFieldsGroup = styled(FlexBox)`
  padding-right: ${pxToVw(16)};
  border-right: solid 1px ${({ theme }) => theme.colors.gray200};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      width: 100%;
      padding-right: 0;
      border-right: none;
    `}
  `}
`

const StyledAmountInputWrap = styled(FlexBox)`
  height: ${({ theme }) => pxToVw(theme.units.buttonSize.large)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      height: ${({ theme }) => pxToVw(theme.units.buttonSize.large, 720)};
    `}
  `}
`

const StyledInputIconWrap = styled(FlexBox)<{
  $error: boolean
}>`
  border: 1px solid ${({ theme, $error }) => (!$error ? theme.colors.gray200 : theme.colors.red)};
  border-right: 0;
  border-radius: ${({ theme }) =>
    `${pxToVw(theme.units.borderRadius.small)} 0 0 ${pxToVw(theme.units.borderRadius.small)}`};
  padding-inline: ${pxToVw(13)} ${pxToVw(0)};
  background-color: ${({ theme }) => theme.colors.bg};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      border-radius: ${({ theme }) =>
        `${pxToVw(theme.units.borderRadius.small, 720)} 0 0 ${pxToVw(
          theme.units.borderRadius.small,
          720
        )}`};
      padding-inline: ${pxToVw(13, 720)} ${pxToVw(0)};
    `}
  `}
`

const StyledInputText = styled(InputText)`
  flex: 1;
  width: ${pxToVw(180)};
  height: ${({ theme }) => pxToVw(theme.units.buttonSize.large)};
  font-size: ${({ theme }) => pxToVw(theme.units.fontSize.body3)};
  border-radius: ${({ theme }) =>
    `0 ${pxToVw(theme.units.borderRadius.small)} ${pxToVw(theme.units.borderRadius.small)} 0`};
  border-left: 0;
  outline: none;

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray600};
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      height: ${({ theme }) => pxToVw(theme.units.buttonSize.large, 720)};
      font-size: ${({ theme }) => pxToVw(theme.units.fontSize.body3, 720)};
      border-radius: ${({ theme }) =>
        `0 ${pxToVw(theme.units.borderRadius.small, 720)} ${pxToVw(
          theme.units.borderRadius.small,
          720
        )} 0`};
    `}
  `}
`

const StyledButton = styled(PrimaryButton)<{
  disabled?: boolean
}>`
  font-size: ${pxToVw(18)};
  opacity: ${({ disabled }) => (disabled ? 0.8 : 1)};
  flex: 1;

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      font-size: ${pxToVw(18, 720)};
    `}
  `}
`

const StyledAmountButton = styled(StyledButton)`
  background-color: ${({ theme }) => theme.colors.bg};
  padding-inline: ${pxToVw(16)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      flex: 0 1 auto;
      padding-inline: ${pxToVw(16, 720)};
    `}
  `}
`

const StyledButtonWrap = styled(FlexBox)`
  padding-left: ${pxToVw(16)};

  > div:first-child {
    display: inline-flex;
    width: 100%;
  }

  label {
    font-size: ${pxToVw(18)};
    color: ${({ theme }) => theme.colors.gray700};
    white-space: nowrap;
    user-select: none;

    > input[type='checkbox'] {
      margin: 0;
    }
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      padding-left: 0;
      width: 100%;

      label {
        font-size: ${pxToVw(18, 720)};
      }
    `}
  `}
`

const StyledFormError = styled.div`
  display: inline-flex;
  font-size: ${pxToVw(18)};
  font-weight: 500;
  // margin-top: ${pxToVw(10)};
  padding-block: 0 ${pxToVw(10)};
  color: ${({ theme }) => theme.colors.red};
  background-color: ${({ theme }) => transparentize(0.33, theme.colors.bg)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      font-size: ${pxToVw(18, 720)};
      // margin-top: ${pxToVw(10, 720)};
      padding-block: 0 ${pxToVw(10, 720)};
    `}
  `}
`

const StyledSlider = styled.div`
  position: absolute;
  background-color: #fff;
  overflow: hidden;
  top: ${pxToVw(141)};
  left: ${pxToVw(1256)};
  width: ${pxToVw(200)};
  height: ${pxToVw(163)};
  border-radius: ${pxToVw(27)} ${pxToVw(27)} ${pxToVw(25)} ${pxToVw(24)};

  .react-slideshow-container .nav {
    opacity: 0;
    transition: opacity 0.5s;

    &:hover {
      opacity: 1;
    }
  }

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    border-radius: ${pxToVw(27)} ${pxToVw(27)} ${pxToVw(25)} ${pxToVw(24)};
    box-shadow: inset 0 ${pxToVw(1)} ${pxToVw(8)} rgba(67, 67, 67, 0.25),
      inset 0 ${pxToVw(-4)} ${pxToVw(8)} rgba(77, 77, 77, 0.25);
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      top: ${pxToVw(827, 720)};
      left: ${pxToVw(288, 720)};
      width: ${pxToVw(164, 720)};
      height: ${pxToVw(136, 720)};
      border-radius: ${pxToVw(20, 720)};

      &::after {
        border-radius: ${pxToVw(20, 720)};
        box-shadow: inset 0 ${pxToVw(1, 720)} ${pxToVw(8, 720)} rgba(67, 67, 67, 0.25),
          inset 0 ${pxToVw(-4, 720)} ${pxToVw(8, 720)} rgba(77, 77, 77, 0.25);
      }
    `}
  `}
`

const StyledSliderImage = styled.img`
  width: ${pxToVw(200)};
  height: ${pxToVw(163)};
  border-radius: ${pxToVw(27)} ${pxToVw(27)} ${pxToVw(25)} ${pxToVw(24)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      width: ${pxToVw(164, 720)};
      height: ${pxToVw(136, 720)};
      border-radius: ${pxToVw(20, 720)};
    `}
  `}
`

// const StyledQuestionCircleInText = styled(Icon)`
//   display: inline-block;
//   vertical-align: sub;
//   cursor: pointer;
//   margin-left: ${pxToVw(7)};

//   ${({ theme }) => css`
//     ${theme.mediaQuery.mobile`
//       margin-left: ${pxToVw(7, 720)};
//     `}
//   `}
// `

const StyledQuestionCircle = styled(Icon)`
  cursor: pointer;
`

const StyledTipsArticle = styled.article`
  p {
    font-size: ${({ theme }) => pxToVw(theme.units.fontSize.body1)};
    line-height: ${({ theme }) => pxToEm(28, theme.units.fontSize.body1)};
    color: ${({ theme }) => theme.colors.gray};
    margin-bottom: 1.5625vw;
    margin-top: 0;

    strong {
      font-weight: 500;
      color: inherit;
    }
  }

  a,
  a:visited {
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      > p {
        font-size: ${pxToVw(25, 720)};
      }
    `}
  `}
`

const StyledTipsIcon = styled.span`
  display: inline-flex;
`

const StyledEasterEggs = styled.div`
  // background: rgba(255, 0, 0, 0.33);
  width: ${pxToVw(215)};
  height: ${pxToVw(75)};
  position: absolute;
  top: ${pxToVw(327)};
  left: ${pxToVw(1251)};

  > div {
    // background: rgba(255, 255, 255, 0.33);
    width: ${pxToVw(36)};
    height: ${pxToVw(36)};
    border-radius: ${pxToVw(18)};
    position: absolute;
    top: ${pxToVw(19)};
    cursor: pointer;

    &:nth-child(1) {
      left: ${pxToVw(20)};
    }
    &:nth-child(2) {
      left: ${pxToVw(71)};
    }
    &:nth-child(3) {
      right: ${pxToVw(59)};
    }
    &:nth-child(4) {
      right: ${pxToVw(10)};
    }
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      // background: rgba(255,0,0,.33);
      width: ${pxToVw(172, 720)};
      height: ${pxToVw(61, 720)};
      top: ${pxToVw(979, 720)};
      left: ${pxToVw(286, 720)};

      > div {
        // background: rgba(255,255,255,.33);
        width: ${pxToVw(30, 720)};
        height: ${pxToVw(30, 720)};
        border-radius: ${pxToVw(15, 720)};
        top: ${pxToVw(14, 720)};

        &:nth-child(1) {
          left: ${pxToVw(15, 720)};
        }
        &:nth-child(2) {
          left: ${pxToVw(55, 720)};
        }
        &:nth-child(3) {
          right: ${pxToVw(47, 720)};
        }
        &:nth-child(4) {
          right: ${pxToVw(8, 720)};
        }
      }
    `}
  `}
`
